/**
 * App Providers
 *
 * Combines all application-level providers in the correct order
 */

import { FC, ReactNode, useEffect } from 'react';
import { Provider as ReduxProvider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { BrowserRouter } from 'react-router-dom';
import { persistor, store } from 'store/store';
import { ThemeProvider } from '@/design-system/themes/theme-provider';
import { sessionManager, AUTH_EVENT, onAuthEvent, getAccessToken } from '@/core/auth';
import {
  initialize,
  connect,
  disconnect,
  destroy,
  on,
  registerBusinessEventHandlers,
} from '@/core/realtime';
import { queryClient } from '@/state/query/query-client';

interface AppProvidersProps {
  children: ReactNode;
}

/**
 * AuthInitializer component
 *
 * Initializes session management on app load.
 * Handles proactive token refresh to prevent 401 errors.
 */
const AuthInitializer: FC<{ children: ReactNode }> = ({ children }) => {
  useEffect(() => {
    // Start session management (proactive token refresh)
    sessionManager.start();

    // Cleanup on unmount
    return () => {
      sessionManager.stop();
    };
  }, []);

  return <>{children}</>;
};

/**
 * RealtimeInitializer component
 *
 * Initializes the Socket.IO connection after authentication and tears it
 * down on logout / session expiry.  Also registers the pre-wired business
 * event handlers so that notifications and React Query cache invalidations
 * happen automatically for policy, claim, and customer events.
 */
const RealtimeInitializer: FC<{ children: ReactNode }> = ({ children }) => {
  useEffect(() => {
    // Configure socket options (no autoConnect — we drive connect manually)
    initialize({
      autoConnect: false,
      autoReconnect: true,
      debug: process.env.NODE_ENV === 'development',
    });

    // Pre-wire business event handlers (notifications + cache invalidation)
    const cleanupHandlers = registerBusinessEventHandlers({ on }, queryClient);

    // Connect / disconnect in response to auth state changes
    const unsubscribeLogin = onAuthEvent(AUTH_EVENT.LOGIN_SUCCESS, () => {
      connect().catch(console.error);
    });

    const unsubscribeLogout = onAuthEvent(AUTH_EVENT.LOGOUT, () => {
      disconnect();
    });

    const unsubscribeExpired = onAuthEvent(AUTH_EVENT.SESSION_EXPIRED, () => {
      disconnect();
    });

    // If the user is already authenticated (page reload), connect immediately
    if (getAccessToken()) {
      connect().catch(console.error);
    }

    return () => {
      cleanupHandlers();
      unsubscribeLogin();
      unsubscribeLogout();
      unsubscribeExpired();
      destroy();
    };
  }, []);

  return <>{children}</>;
};

/**
 * AppProviders component
 *
 * Wraps the application with all necessary providers:
 * 1. Redux Provider (state management)
 * 2. PersistGate (state persistence)
 * 3. ThemeProvider (design system theming)
 * 4. BrowserRouter (routing)
 * 5. AuthInitializer (session management)
 * 6. RealtimeInitializer (Socket.IO connection)
 */
export const AppProviders: FC<AppProvidersProps> = ({ children }) => {
  return (
    <ReduxProvider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeProvider>
          <BrowserRouter>
            <AuthInitializer>
              <RealtimeInitializer>{children}</RealtimeInitializer>
            </AuthInitializer>
          </BrowserRouter>
        </ThemeProvider>
      </PersistGate>
    </ReduxProvider>
  );
};
